<template>
<div class="page">
  <div class="page-content">
    <div class="row justify-content-center align-content-center align-items-center mi-trabajo-sin-lista">
      <i class="f7-icons text-color-gray">tray</i>
      <span class="titulo">{{ titulo }}</span>
      <span class="detalle">{{ motivo }}</span>
      <a class="button button-fill button-large" v-if="reintentable" @click="reintentar">
        Reintentar
      </a>
      <!-- LA SALIDA ES UNA RUTA A LA QUE ESA PERSONA ENTRA. La regla de dirección del 05/10/2026:
           un botón que lleva a un 403 es peor que no tener botón. El Centro de Cobros lo gatea
           `PermisosCobros.ROLES_ENTRADA`, y a esta pantalla llega precisamente quien puede no
           tener ninguno de esos roles —un gestor de cobro al que le han retirado su última factura
           y vuelve por una URL guardada—, así que la salida se decide, no se escribe. -->
      <a class="button button-large" href="/dashboard/cobros/" v-else-if="entraEnElCentroDeCobros">
        Ir al Centro de Cobros
      </a>
      <a class="button button-large" href="/" v-else>Ir al inicio</a>
    </div>
  </div>
</div>
</template>
<script>
import PermisosCobros from './permisos-cobros.js';
import UsuarioActualizado from './../../js/usuarioActualizado.js';

/**
 * Lo que se enseña en lugar de «Mi trabajo de hoy» (COB-4, #190) cuando esa pantalla no se puede
 * abrir. Dos casos, y **no son el mismo**:
 *
 *  1. **No entras** (`reintentable: false`). La pantalla es la lista diaria de gestión de cobro:
 *     entran Administración, Dirección y Administrador, y cada gestor de cobro con su cartera
 *     (criterio 10). Quien no tiene ninguna factura asignada como gestor **no tiene ninguna lista
 *     que mirar**, y eso no es un problema de permisos: se arregla pidiéndole a Administración que
 *     le asigne facturas. Por eso el texto lo manda el servidor —es él quien sabe por qué— y por eso
 *     no se enseña el 403 genérico, que diría «sin permisos» y mandaría a buscar donde no es.
 *  2. **No se ha podido comprobar** (`reintentable: true`): error, timeout o un cuerpo que no se
 *     sabe leer. Un fallo no vale por «entra igual», así que aquí se reintenta y no se abre nada.
 *
 * En el caso normal nadie llega aquí, porque la regla de pantalla de dirección del 05/10/2026 dice
 * que un punto de entrada que lleva a un 403 no se dibuja: la pantalla del Centro de Cobros pregunta
 * lo mismo que esta barrera y solo enseña el enlace a quien entra. Esto es para quien llegue por
 * URL o por un enlace guardado.
 *
 * **Y esa misma regla vale para la salida de esta pantalla**, que es lo que faltaba (revisión
 * CODEX, ronda 5). El botón decía «Ir al Centro de Cobros» para todo el mundo, y a quien llega aquí
 * sin ninguno de los roles de `PermisosCobros.ROLES_ENTRADA` —un gestor de cobro al que le han
 * retirado su última factura, que es exactamente el caso del `reintentable: false`— ese botón lo
 * mandaba al `Error403` que la regla existe para evitar. Ahora la salida se decide con la misma
 * pregunta que la ruta de destino, y para quien no entra ahí la salida es el inicio.
 *
 * Hermano de `sesion-no-disponible.vue` (#65) y de `calendario/calendario-no-disponible.vue` (#49):
 * misma forma, otro motivo.
 */
export default {
  props: {
    /** URL que se intentaba abrir, para volver a intentarlo tal cual. */
    url: { type: String, default: '/dashboard/cobros/' },
    /** Por qué no se abre, dicho por el servidor. */
    motivo: { type: String, required: true },
    /** Si tiene sentido volver a intentarlo: solo cuando el fallo fue de comunicación. */
    reintentable: { type: Boolean, default: false }
  },
  data: function() {
    return {
      /**
       * Si esta persona entra en el Centro de Cobros, que es lo que decide a dónde sale de aquí.
       *
       * **Arranca en `false` y lo sube la respuesta**, no al revés: fail-closed, como la barrera de
       * la ruta. Mientras no se sepa, la salida es el inicio, que es una ruta a la que entra
       * cualquiera (`RoutesUtils.tieneAcceso` la deja pasar siempre); dar por buena la entrada al
       * Centro de Cobros «hasta que se demuestre lo contrario» es justo lo que acaba en el 403.
       */
      entraEnElCentroDeCobros: false
    };
  },
  computed: {
    titulo: function() {
      return this.reintentable
        ? 'No se ha podido comprobar tu lista de cobro'
        : 'No tienes ninguna lista de trabajo de cobro';
    }
  },
  /**
   * Se pregunta con el usuario de ESTA carga —`UsuarioActualizado`, el mismo que usa
   * `conUsuarioActualizado` para gatear el Centro de Cobros— y no con el guardado en el navegador:
   * si se decidiera con el de la sesión anterior, el botón volvería a poder llevar a un 403 justo
   * el día que a alguien le cambian los roles, que es el caso que la #65 vino a cerrar.
   *
   * Y con `PermisosCobros.verCentroCobros`, que es la misma función que decide la ruta de destino.
   * Una lista de roles escrita aquí sería una segunda definición de quién entra.
   */
  created: function() {
    const self = this;
    UsuarioActualizado.esperar().then(function(usuario) {
      self.entraEnElCentroDeCobros = PermisosCobros.verCentroCobros(usuario);
    }, function() {
      // Un fallo no vale por «entra igual»: se queda la salida al inicio.
    });
  },
  methods: {
    reintentar: function() {
      const router = this.$f7router
        || (this.$f7 && this.$f7.views && this.$f7.views.main && this.$f7.views.main.router);
      if (!router) return;
      // `reloadCurrent` para sustituir ESTA pantalla y no dejar el error en el historial, e
      // `ignoreCache` para que la ruta se resuelva otra vez. Mismo criterio que
      // `sesion-no-disponible.vue`.
      router.navigate(this.url, { reloadCurrent: true, ignoreCache: true });
    }
  }
};
</script>
<style scoped>
.mi-trabajo-sin-lista { min-height: 100%; flex-direction: column; text-align: center; padding: 24px; }
.mi-trabajo-sin-lista .f7-icons { font-size: 64px; }
.mi-trabajo-sin-lista .titulo { font-size: 1.2rem; font-weight: 600; margin-top: 12px; }
.mi-trabajo-sin-lista .detalle { max-width: 520px; margin-top: 8px; color: #6b7885; line-height: 1.5; }
.mi-trabajo-sin-lista .button { margin-top: 20px; width: 220px; }
</style>
